import axios from "axios";
import { baseURL } from "../Constants";
import { Match } from "../models/Match";
import { PlayerMatch } from "../models/PlayerMatch";
import { HighlightClip } from "../models/HighlightClip";

const url = `${baseURL}/UserDemo`;

export interface UploadedDemo {
    uploadId: string;
    ownerSteamId: string;
    matchId: string | null;
    gameIds: string[];
    originalFilename: string | null;
    mapName: string | null;
    team1Name: string | null;
    team2Name: string | null;
    status: "processing" | "completed" | "failed";
    errorMessage: string | null;
    fileSizeBytes: number | null;
    createdAt: string;
    completedAt: string | null;
}

export interface ShareLink {
    token: string;
    url: string;
    createdAt: string;
    expiresAt: string | null;
    revoked: boolean;
    valid: boolean;
}

const authHeader = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
};

export const uploadDemo = async (
    file: File,
    onProgress?: (percent: number, loaded: number, total: number) => void,
    signal?: AbortSignal
): Promise<UploadedDemo> => {
    const form = new FormData();
    form.append("file", file);
    const response = await axios.post<UploadedDemo>(`${url}/upload`, form, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        timeout: 0, // no timeout on the upload transfer; the parse now runs async server-side
        signal,
        onUploadProgress: (e) => {
            if (onProgress && e.total) {
                onProgress(Math.round((e.loaded / e.total) * 100), e.loaded, e.total);
            }
        },
    });
    return response.data;
};

export interface AdminUploadedDemo {
    uploadId: string;
    ownerSteamId: string;
    ownerName: string | null;
    matchId: string | null;
    mapName: string | null;
    team1Name: string | null;
    team2Name: string | null;
    originalFilename: string | null;
    status: string;
    errorMessage: string | null;
    fileSizeBytes: number | null;
    createdAt: string;
    completedAt: string | null;
}

// Admin: every uploaded demo across all users.
export const getAllUploadedDemos = async (): Promise<AdminUploadedDemo[]> => {
    const response = await axios.get<AdminUploadedDemo[]>(`${url}/admin/all`, { headers: authHeader() });
    return response.data;
};

export const getMyDemos = async (): Promise<UploadedDemo[]> => {
    const response = await axios.get<UploadedDemo[]>(`${url}/mine`, { headers: authHeader() });
    return response.data;
};

// Team names this donator has used before — powers the upload-naming autocomplete.
export const getMyTeamNames = async (): Promise<string[]> => {
    const response = await axios.get<string[]>(`${url}/myTeams`, { headers: authHeader() });
    return response.data;
};

// Names an uploaded match's two teams (stable, owner-scoped). Returns the re-hydrated match(es).
export const setUploadTeamNames = async (
    uploadId: string,
    team1Name: string,
    team2Name: string
): Promise<Match[]> => {
    const response = await axios.post<Match[]>(
        `${url}/${uploadId}/teams`,
        { team1Name, team2Name },
        { headers: authHeader() }
    );
    return response.data;
};

const tokenQuery = (token?: string) => (token ? `?token=${encodeURIComponent(token)}` : "");

export const getUpload = async (uploadId: string, token?: string): Promise<UploadedDemo> => {
    const response = await axios.get<UploadedDemo>(`${url}/${uploadId}${tokenQuery(token)}`, {
        headers: token ? {} : authHeader(),
    });
    return response.data;
};

export const getSharedUpload = async (token: string): Promise<UploadedDemo> => {
    const response = await axios.get<UploadedDemo>(`${url}/shared/${encodeURIComponent(token)}`);
    return response.data;
};

export const getUploadMatch = async (uploadId: string, token?: string): Promise<Match[]> => {
    const response = await axios.get<Match[]>(`${url}/${uploadId}/match${tokenQuery(token)}`, {
        headers: token ? {} : authHeader(),
    });
    return response.data;
};

export const getUploadPlayerMatches = async (
    uploadId: string,
    gameId: string,
    token?: string
): Promise<PlayerMatch[]> => {
    const response = await axios.get<PlayerMatch[]>(
        `${url}/${uploadId}/playerMatches/${gameId}${tokenQuery(token)}`,
        { headers: token ? {} : authHeader() }
    );
    return response.data;
};

export const getUploadPlayerMatch = async (
    uploadId: string,
    gameId: string,
    steamId: string,
    token?: string
): Promise<PlayerMatch> => {
    const response = await axios.get<PlayerMatch>(
        `${url}/${uploadId}/playerMatch/${gameId}/${steamId}${tokenQuery(token)}`,
        { headers: token ? {} : authHeader() }
    );
    return response.data;
};

export const createShareLink = async (uploadId: string, expiresInDays?: number): Promise<ShareLink> => {
    const response = await axios.post<ShareLink>(
        `${url}/${uploadId}/share`,
        { expiresInDays },
        { headers: authHeader() }
    );
    return response.data;
};

export const getShareLinks = async (uploadId: string): Promise<ShareLink[]> => {
    const response = await axios.get<ShareLink[]>(`${url}/${uploadId}/shares`, { headers: authHeader() });
    return response.data;
};

export const revokeShareLink = async (uploadId: string, token: string): Promise<void> => {
    await axios.delete(`${url}/${uploadId}/share/${token}`, { headers: authHeader() });
};

export const deleteUpload = async (uploadId: string): Promise<void> => {
    await axios.delete(`${url}/${uploadId}`, { headers: authHeader() });
};

// Replay fetch routed through the access-controlled endpoint (used by the private viewer).
export const checkUploadReplayAvailability = async (
    uploadId: string,
    gameId: string,
    token?: string
): Promise<{ available: boolean; roundCount: number }> => {
    // Availability is gameid-scoped; reuse the public availability check (metadata is not sensitive).
    const response = await axios.get<{ available: boolean; roundCount: number }>(
        `${baseURL}/Replay/available/${gameId}`
    );
    return response.data;
};

// ---- Clip creation on uploaded demos (donator perk) --------------------------------------

export interface HighlightCandidate {
    id: string; // "{steamId}_{round}_{type}"
    steamId: string;
    playerName: string;
    type: string;
    roundNumber: number;
    killCount: number;
    durationSeconds: number;
}

export interface ClipQuota {
    used: number;
    limit: number;      // -1 = unlimited (admin)
    remaining: number;  // -1 = unlimited
    unlimited: boolean;
}

export interface UploadClipJob {
    id: number;
    status: string;
    selectedCount: number;
    createdAt: string;
    completedAt: string | null;
}

export const getUploadHighlights = async (uploadId: string): Promise<HighlightCandidate[]> => {
    const r = await axios.get<HighlightCandidate[]>(`${url}/${uploadId}/highlights`, { headers: authHeader() });
    return r.data;
};

export const getClipQuota = async (): Promise<ClipQuota> => {
    const r = await axios.get<ClipQuota>(`${url}/clips/quota`, { headers: authHeader() });
    return r.data;
};

export const createUploadClips = async (uploadId: string, highlightIds: string[]): Promise<{ jobId: number; queued: number }> => {
    const r = await axios.post(`${url}/${uploadId}/clips`, { highlightIds }, { headers: authHeader() });
    return r.data;
};

export const getUploadClips = async (uploadId: string): Promise<{ clips: HighlightClip[]; jobs: UploadClipJob[] }> => {
    const r = await axios.get<{ clips: HighlightClip[]; jobs: UploadClipJob[] }>(`${url}/${uploadId}/clips`, { headers: authHeader() });
    return r.data;
};

export const fetchUploadReplayRound = async (
    uploadId: string,
    gameId: string,
    roundNumber: number,
    token?: string
): Promise<ArrayBuffer> => {
    const response = await axios.get(
        `${url}/${uploadId}/replay/${gameId}/${roundNumber}${tokenQuery(token)}`,
        { responseType: "arraybuffer", headers: token ? {} : authHeader() }
    );
    return response.data;
};
